/**
 * KEV dashboard — the customer/product overview.
 *
 * One KPI row and ONE grouped table. The operator chooses the grouping
 * (`Nach Kunde` / `Nach Produkt`); a group expands into its ProduktInstanzen,
 * an instance into its components. Notifying is a selection: the operator
 * ticks exactly the instances that should be notified and sends them from the
 * selection bar — the server drafts one advisory per Kunde covering only the
 * ticked instances (see dashboardGroups.ts for the selection rules).
 *
 * What it keeps from the React port of app/web/dashboard.html, and why:
 *
 *   - ONE state for BOTH payloads. `GET /api/dashboard/overview` alone cannot
 *     decide whether a customer may be notified: that needs a VulnerabilityEvent
 *     of this ProduktInstanz with the verdict BETROFFEN, which lives in
 *     `GET /api/dashboard/vulnerability-events`. See `notifyGate`.
 *   - `only_affected` is the ONLY filter that hits the server; free text and
 *     "Nur unbenachrichtigte" are client-side over the loaded rows, so typing
 *     never triggers a CMDBuild round trip.
 *   - Automatic refresh (60 s) is paused while the advisory dialog is open — see
 *     useDashboardData for the stale-closure and cleanup details.
 *
 * Permissions (frozen vocabulary):
 *   dashboard.read     — read this page (the API enforces it),
 *   triage.read        — the "Zur Bewertung" links and the lazy KEV/EPSS lookup,
 *   notification.send  — the selection checkboxes, "Auswahl benachrichtigen"
 *                        and "Als versendet markieren".
 * An auditor holds only the read permissions: every mutating control stays
 * visible but disabled and carries a German title naming the missing right.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import Banner from '../components/Banner';
import EmptyState from '../components/EmptyState';
import { KpiRow, type KpiItem } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import Spinner, { PageSpinner } from '../components/Spinner';
import { errorMessage, notifyProdukte } from '../lib/api';
import { useAuth } from '../lib/auth';
import { countLabel, fmtDate } from '../lib/format';
import { readOnlyNotice } from '../lib/permissions';
import type {
  NotifyResult,
  OverviewRow,
  VulnerabilityEventRow,
} from '../lib/types';
import './dashboard/dashboard.css';
import {
  customerCount,
  effectiveSelection,
  groupRows,
  type GroupMode,
} from './dashboard/dashboardGroups';
import { GroupTable } from './dashboard/GroupCells';
import NotifyDialog from './dashboard/NotifyDialog';
import {
  kpiCounters,
  matchesFilters,
  notifyGate,
  openBenachrichtigungIds,
  orFallback,
  searchTokens,
  type NotifyGate,
} from './dashboard/dashboardModel';
import { useDashboardData } from './dashboard/useDashboardData';
import { useExploitability } from './dashboard/useExploitability';

/** Shown under every failed load — the two things that are actually wrong. */
const CMDB_HINT = 'Prüfen Sie die Verbindung zu CMDBuild und ob das Schema aktuell ist.';

/** Stable empty defaults so the memos below do not see a new array every render. */
const EMPTY_ROWS: readonly OverviewRow[] = [];
const EMPTY_EVENTS: readonly VulnerabilityEventRow[] = [];

/** State of the advisory dialog. `busy` covers the notify request itself. */
interface NotifyDialogState {
  produktIds: number[];
  label: string;
  busy: boolean;
  results: NotifyResult[] | null;
  error: string | null;
}

/** Remembered per viewer; storage may be unavailable (private mode, previews). */
const MODE_STORAGE_KEY = 'dashboard.groupMode';

function readMode(): GroupMode {
  try {
    return window.localStorage.getItem(MODE_STORAGE_KEY) === 'produkt' ? 'produkt' : 'kunde';
  } catch {
    return 'kunde';
  }
}

function writeMode(mode: GroupMode): void {
  try {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    // Only a convenience — the page works without it.
  }
}

export function DashboardPage(): ReactNode {
  const { has } = useAuth();
  const navigate = useNavigate();

  const canNotify = has('notification.send');
  const canReadTriage = has('triage.read');

  const [mode, setModeState] = useState<GroupMode>(readMode);
  const [onlyAffected, setOnlyAffected] = useState(true);
  const [onlyUnnotified, setOnlyUnnotified] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const [dialog, setDialog] = useState<NotifyDialogState | null>(null);

  const setMode = useCallback((next: GroupMode) => {
    setModeState(next);
    writeMode(next);
  }, []);

  const { data, loading, error, syncStatus, refresh } = useDashboardData({
    onlyAffected,
    // A refresh behind the open advisory would swap the data under the reader.
    paused: dialog !== null,
  });

  // KEV/EPSS facts are fetched only for rows the user expands, and only with the
  // permission the endpoint requires.
  const exploitability = useExploitability(canReadTriage);

  const rows = data?.overview.rows ?? EMPTY_ROWS;
  const events = data?.events.events ?? EMPTY_EVENTS;

  // The gate is a cross-payload join; compute it once per payload, not per cell.
  const gates = useMemo(() => {
    const map = new Map<number, NotifyGate>();
    for (const row of rows) map.set(row.produkt.id, notifyGate(row, events));
    return map;
  }, [rows, events]);

  const tokens = useMemo(() => searchTokens(search), [search]);
  const visibleRows = useMemo(
    () => rows.filter((row) => matchesFilters(row, tokens, onlyUnnotified)),
    [rows, tokens, onlyUnnotified],
  );
  const groups = useMemo(() => groupRows(visibleRows, mode, gates), [visibleRows, mode, gates]);

  const counters = useMemo(
    () => (data === null ? [] : kpiCounters(data.overview.summary, data.events.offen)),
    [data],
  );

  // Only „Zu benachrichtigen“ switches a list filter („Nur unbenachrichtigte“).
  // The affected-customer/-delivery tiles stay plain figures: the list shows
  // only affected entries by default, and Kunde/Produkt is the view switch's
  // job. Like the Schwachstellen page, the tile stays marked while exactly its
  // filter is active, and a second click removes it again.
  const kpiTiles = useMemo((): KpiItem[] => {
    const plain = search.trim() === '';
    const targets: Readonly<Record<string, { active: boolean; apply: () => void; clear: () => void }>> = {
      'Zu benachrichtigen': {
        active: plain && onlyAffected && onlyUnnotified,
        apply: () => { setOnlyAffected(true); setOnlyUnnotified(true); setSearch(''); },
        clear: () => setOnlyUnnotified(false),
      },
    };
    return counters.map((counter) => {
      // Open assessments live in the triage queue (its default view), not in
      // this list of customers/products, so the tile takes you there.
      if (counter.label === 'Offene Bewertungen') {
        return {
          ...counter,
          ariaLabel: `Offene Bewertungen: ${counter.value} in der Triage öffnen`,
          onClick: () => navigate('/triage'),
        };
      }
      const target = targets[counter.label];
      return target
        ? {
            ...counter,
            ariaLabel: `${counter.label}: ${counter.value} anzeigen`,
            selected: target.active,
            controls: 'dash-results',
            onClick: target.active ? target.clear : target.apply,
          }
        : counter;
    });
  }, [counters, navigate, onlyAffected, onlyUnnotified, search]);

  /* --------------------------------------------------------------------- *
   * Selection
   * --------------------------------------------------------------------- */

  // Validated against the live payload: a refresh can drop an instance or
  // make it unselectable, and neither may be sent silently.
  const selection = useMemo(
    () => effectiveSelection(rows, gates, selected),
    [rows, gates, selected],
  );
  const selectionCustomers = useMemo(() => customerCount(selection), [selection]);

  const onSelect = useCallback((ids: readonly number[], checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  /* --------------------------------------------------------------------- *
   * Mutations
   * --------------------------------------------------------------------- */

  const notify = useCallback(
    (targets: readonly OverviewRow[]): void => {
      if (targets.length === 0) return;
      const produktIds = targets.map((row) => row.produkt.id);
      const label = `${countLabel(produktIds.length, 'Produkt', 'Produkte')} · ${countLabel(
        customerCount(targets),
        'Kunde',
        'Kunden',
      )}`;
      setDialog({ produktIds, label, busy: true, results: null, error: null });
      void (async () => {
        try {
          const response = await notifyProdukte({ produkt_ids: produktIds });
          setDialog((current) =>
            current === null || current.produktIds !== produktIds
              ? current
              : { ...current, busy: false, results: response.results, error: null },
          );
          // Only the notified ids leave the selection — a single-row notify
          // must not wipe what the operator ticked elsewhere.
          onSelect(produktIds, false);
          // The notify call records (and stamps) Benachrichtigung cards, so both
          // payloads are stale now — the dialog's action buttons read the fresh
          // ones.
          await refresh();
        } catch (err) {
          const message = errorMessage(err);
          setDialog((current) =>
            current === null || current.produktIds !== produktIds
              ? current
              : { ...current, busy: false, error: message },
          );
        }
      })();
    },
    [onSelect, refresh],
  );

  const onNotifySelection = useCallback((): void => notify(selection), [notify, selection]);
  const onNotifyOne = useCallback((row: OverviewRow): void => notify([row]), [notify]);

  /* --------------------------------------------------------------------- *
   * Dialog wiring — the stamp ids come from the LIVE payload
   * --------------------------------------------------------------------- */

  const rowsById = useMemo(() => new Map(rows.map((row) => [row.produkt.id, row])), [rows]);

  const openIdsFor = useCallback(
    (produktIds: readonly number[]): number[] =>
      produktIds.flatMap((id) => {
        const row = rowsById.get(id);
        return row === undefined ? [] : openBenachrichtigungIds(row);
      }),
    [rowsById],
  );

  const instanceName = useCallback(
    (produktId: number | null): string => {
      const row = produktId === null ? undefined : rowsById.get(produktId);
      if (row === undefined) return `Produkt #${produktId ?? '?'}`;
      const name = orFallback(row.produkttyp.bezeichnung, 'Produkt');
      return row.produkt.seriennummer ? `${name} S/N ${row.produkt.seriennummer}` : name;
    },
    [rowsById],
  );

  const closeDialog = useCallback(() => setDialog(null), []);
  const onMarked = useCallback(() => {
    void refresh();
  }, [refresh]);

  /* --------------------------------------------------------------------- *
   * Render
   * --------------------------------------------------------------------- */

  const emptyText =
    rows.length === 0
      ? onlyAffected
        ? 'Keine Produktinstanzen mit KEV-Treffer. Deaktivieren Sie „Nur betroffene“, um alle anzuzeigen.'
        : 'Keine Produktinstanzen vorhanden. Unter „Stammdaten“ anlegen oder per CSV importieren.'
      : 'Nichts passt zum Filter.';

  const busy = dialog !== null && dialog.busy;

  return (
    <>
      <PageHeader
        title="Dashboard"
        sub={data === null ? undefined : `Stand ${fmtDate(data.overview.generated_at)}`}
        actions={loading && data !== null ? <Spinner label="Daten werden geladen…" /> : null}
      />

      {syncStatus !== null ? (
        <Banner kind={syncStatus === 'retrying' ? 'warn' : 'info'}>
          {syncStatus === 'retrying'
            ? 'Die Übertragung an Dependency-Track ist fehlgeschlagen und wird automatisch wiederholt.'
            : 'Änderungen werden an Dependency-Track übertragen. Ergebnisse folgen nach der Analyse.'}
        </Banner>
      ) : null}

      {error !== null ? (
        <Banner kind="error" sub={CMDB_HINT}>
          Übersicht konnte nicht geladen werden: {error}
        </Banner>
      ) : null}

      {/*
        Read-only accounts (the auditor role) get the same explicit notice the
        triage page shows, instead of only a row of disabled checkboxes whose
        tooltips have to be hunted for.
      */}
      {!canNotify ? (
        <Banner kind="info">{readOnlyNotice('notification.send')}</Banner>
      ) : null}

      {data === null ? (
        loading ? (
          <PageSpinner text="Übersicht wird geladen…" />
        ) : (
          <EmptyState title="Keine Daten geladen" />
        )
      ) : (
        <>
          <KpiRow counters={kpiTiles} label="Kennzahlen zur aktuellen Sicherheitslage" />

          <section id="dash-results" className="card dash-worklist">
            <div className="card-head table-head">
              <h2>{mode === 'kunde' ? 'Übersicht je Kunde' : 'Übersicht je Produkt'}</h2>
              <div className="seg view-switch" role="group" aria-label="Gruppierung wählen">
                <button
                  type="button"
                  className={mode === 'kunde' ? 'on' : undefined}
                  aria-pressed={mode === 'kunde'}
                  onClick={() => setMode('kunde')}
                >
                  Nach Kunde
                </button>
                <button
                  type="button"
                  className={mode === 'produkt' ? 'on' : undefined}
                  aria-pressed={mode === 'produkt'}
                  onClick={() => setMode('produkt')}
                >
                  Nach Produkt
                </button>
              </div>
            </div>

            <div className="table-toolbar">
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                placeholder="Kunde, Produkt, Komponente oder CVE suchen…"
                aria-label="Übersicht filtern"
              />
              <div className="table-toolbar-options">
                <label className="check" title="Serverseitiger Filter: lädt die Übersicht neu">
                  <input
                    type="checkbox"
                    checked={onlyAffected}
                    onChange={(event) => setOnlyAffected(event.currentTarget.checked)}
                  />
                  Nur betroffene
                </label>
                <label
                  className="check"
                  title="Betroffene Auslieferungen, für die noch keine Benachrichtigung versendet wurde"
                >
                  <input
                    type="checkbox"
                    checked={onlyUnnotified}
                    onChange={(event) => setOnlyUnnotified(event.currentTarget.checked)}
                  />
                  Nur unbenachrichtigte
                </label>
                <span className="table-count" aria-live="polite">
                  {countLabel(groups.length, mode === 'kunde' ? 'Kunde' : 'Produkt', mode === 'kunde' ? 'Kunden' : 'Produkte')}
                </span>
              </div>
            </div>

            <GroupTable
              mode={mode}
              groups={groups}
              store={exploitability}
              gates={gates}
              selected={selected}
              canNotify={canNotify}
              canReadTriage={canReadTriage}
              onSelect={onSelect}
              onNotifyOne={onNotifyOne}
              emptyText={emptyText}
              paginationResetKey={JSON.stringify([mode, onlyAffected, onlyUnnotified, search])}
            />

            {/* One instance is sent from its own row button; the bar is for batches. */}
            {selection.length > 1 ? (
              <div className="dash-selectionbar" role="region" aria-label="Auswahl">
                <span>
                  <strong>{countLabel(selection.length, 'Produkt', 'Produkte')}</strong> ausgewählt
                  <span className="muted">
                    {' '}
                    · {countLabel(selectionCustomers, 'Anschreiben', 'Anschreiben')} an{' '}
                    {countLabel(selectionCustomers, 'Kunde', 'Kunden')}
                  </span>
                </span>
                <span className="spacer" />
                <button type="button" className="ghost small" onClick={clearSelection} disabled={busy}>
                  Auswahl aufheben
                </button>
                <button
                  type="button"
                  className="primary small"
                  onClick={onNotifySelection}
                  disabled={!canNotify || busy}
                  title="Benachrichtigungen für genau diese Produkte erfassen und Anschreiben erzeugen"
                >
                  {busy ? <Spinner variant="on-accent" /> : null} Auswahl benachrichtigen
                </button>
              </div>
            ) : null}
          </section>
        </>
      )}

      {dialog !== null ? (
        <NotifyDialog
          label={dialog.label}
          busy={dialog.busy}
          results={dialog.results}
          error={dialog.error}
          openIdsFor={openIdsFor}
          instanceName={instanceName}
          canNotify={canNotify}
          onClose={closeDialog}
          onMarked={onMarked}
        />
      ) : null}
    </>
  );
}

export default DashboardPage;
